<template>
	<v-app>
		<v-layout class="mobile-layout">
			<!-- Compact App Bar -->
			<v-app-bar density="compact" color="primary" elevation="0" class="app-bar-safe">
				<template v-if="canGoBack && isLoggedIn">
					<v-btn icon variant="text" @click="$router.back()">
						<v-icon>mdi-chevron-left</v-icon>
					</v-btn>
				</template>
				<v-avatar v-else size="30" class="ml-3 app-bar-logo">
					<v-img :src="logoSrc" alt="NomNomNomads" />
				</v-avatar>

				<v-app-bar-title class="app-bar-title">
					<div class="app-bar-titlebox">
						<span v-if="brandEyebrow" class="app-bar-title__eyebrow">NomNomNomads</span>
						<span class="app-bar-title__text">{{ currentPageTitle }}</span>
					</div>
				</v-app-bar-title>

				<template v-if="isLoggedIn && showCircleSwitcher">
					<v-menu>
						<template v-slot:activator="{ props }">
							<v-btn v-bind="props" icon variant="text" size="small">
								<v-icon>mdi-swap-horizontal</v-icon>
							</v-btn>
						</template>
						<v-list density="compact">
							<v-list-subheader>Switch Circle</v-list-subheader>
							<v-list-item v-for="circle in userCircles" :key="circle.id" :active="activeCircle?.id === circle.id" @click="onCircleSelected(circle.id)">
								<template v-slot:prepend>
									<v-icon v-if="activeCircle?.id === circle.id" color="primary" size="small">mdi-check</v-icon>
									<v-icon v-else size="small" color="transparent">mdi-check</v-icon>
								</template>
								<v-list-item-title>{{ circle.name }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</template>
			</v-app-bar>

			<!-- Main Content - No container padding for mobile -->
			<v-main class="main-content">
				<!-- Plain router-view: vue-instyle's PageTransition pulls a second (v4) vue-router
				     copy whose useRouter() returns undefined against the app's v5 router and crashes
				     at boot. Restore PageTransition once vue-instyle's vue-router is a true peer dep. -->
				<router-view />
			</v-main>

			<!-- Bottom Navigation for Mobile -->
			<v-bottom-navigation v-if="isLoggedIn" grow color="primary" class="bottom-nav-safe">
				<v-btn to="/" :value="1">
					<v-icon>mdi-home-outline</v-icon>
					<span class="text-caption">Home</span>
				</v-btn>
				<v-btn :to="hasActiveCircle ? '/visits' : undefined" :value="2" :disabled="!hasActiveCircle" :class="{ 'nav-disabled': !hasActiveCircle }">
					<v-icon>mdi-calendar-outline</v-icon>
					<span class="text-caption">Visits</span>
				</v-btn>
				<v-btn :to="hasActiveCircle ? '/restaurants' : undefined" :value="3" :disabled="!hasActiveCircle" :class="{ 'nav-disabled': !hasActiveCircle }">
					<v-icon>mdi-silverware-fork-knife</v-icon>
					<span class="text-caption">Restaurants</span>
				</v-btn>
				<v-btn to="/profile" :value="4">
					<v-icon>mdi-account-outline</v-icon>
					<span class="text-caption">Profile</span>
				</v-btn>
			</v-bottom-navigation>
		</v-layout>

		<!-- Global Notification Snackbar -->
		<v-snackbar
			v-model="notificationStore.show"
			:color="notificationStore.color"
			:timeout="notificationStore.timeout"
			location="top"
			multi-line
			:style="{ marginTop: 'calc(48px + env(safe-area-inset-top, 0px))' }"
		>
			{{ notificationStore.message }}
			<template v-slot:actions>
				<v-btn variant="text" @click="notificationStore.hide()"> Close </v-btn>
			</template>
		</v-snackbar>
	</v-app>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCirclesStore } from '@/stores/circles';
import { useNotificationStore } from '@/stores/notifications';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const circlesStore = useCirclesStore();
const notificationStore = useNotificationStore();

const selectedCircleId = ref(null);
const logoSrc = `${process.env.BASE_URL}nomnomnomads.png`;

const pageTitles = {
	'/login': 'Sign In',
	'/register': 'Sign Up',
	'/circles': 'Circles',
	'/profile': 'Profile',
	'/visits/new': 'Plan Visit',
	'/restaurants/new': 'Add Restaurant',
	'/circles/new': 'New Circle'
};

// Pages that should show circle name instead of generic title
const circleContextPages = ['/', '/visits', '/restaurants'];

const isLoggedIn = computed(() => authStore.isLoggedIn);
const userCircles = computed(() => circlesStore.userCircles);
const activeCircle = computed(() => circlesStore.activeCircle);
const hasActiveCircle = computed(() => !!activeCircle.value);
const hasCircleCreatorRole = computed(() => authStore.hasCircleCreatorRole);
const shouldShowNavigation = computed(() => hasActiveCircle.value || hasCircleCreatorRole.value);
const currentPageTitle = computed(() => {
	if (circleContextPages.includes(route.path) && activeCircle.value?.name) {
		return activeCircle.value.name;
	}
	return pageTitles[route.path] || 'NomNomNomads';
});
// Show the "NomNomNomads" eyebrow above the title only when the main title is a
// circle name (so the brand stays present without crowding the bar elsewhere).
const brandEyebrow = computed(() => circleContextPages.includes(route.path) && !!activeCircle.value?.name);
// The 4 bottom-nav tab roots own their navigation via the tab bar, so the back
// button is redundant there — only show it on drill-down detail/form pages (which
// have no other clear way back in a standalone app).
const tabRoots = ['/', '/visits', '/restaurants', '/profile'];
const canGoBack = computed(() => !!router.options.history.state.back && !tabRoots.includes(route.path));
const showCircleSwitcher = computed(() => userCircles.value.length > 1);

function onCircleSelected(circleId) {
	circlesStore.setActiveCircle(circleId);
}

watch(isLoggedIn, async (newVal) => {
	if (newVal) {
		await circlesStore.loadActiveCircle();
		if (activeCircle.value) selectedCircleId.value = activeCircle.value.id;
	}
});

watch(activeCircle, (newVal) => {
	if (newVal) {
		selectedCircleId.value = newVal.id;
	}
});

onMounted(async () => {
	if (isLoggedIn.value) {
		// Sync roles from JWT in case localStorage is stale
		await authStore.checkAuth();

		await circlesStore.loadActiveCircle();

		// If user has no circles, refresh user data from server
		// This ensures roles are up-to-date (e.g., if they just became a circleCreator)
		if (userCircles.value.length === 0) {
			await authStore.refreshUserFromServer();
		}

		if (activeCircle.value) selectedCircleId.value = activeCircle.value.id;
	}
});
</script>

<style>
/* iOS safe area support */
:root {
	--sat: env(safe-area-inset-top);
	--sar: env(safe-area-inset-right);
	--sab: env(safe-area-inset-bottom);
	--sal: env(safe-area-inset-left);
	--keyboard-height: 0px;
}

/* Prevent iOS viewport scrolling - lock html/body */
html,
body {
	position: fixed;
	overflow: hidden;
	width: 100%;
	height: 100%;
	overscroll-behavior: none;
}

/* Vuetify app wrapper */
.v-application {
	position: fixed !important;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	overflow: hidden;
}

/* Apply the warm native font stack across the whole app */
.v-application {
	font-family: var(--app-font);
}

/* Mobile-first layout */
.mobile-layout {
	background-color: #fbf6ef;
	height: 100%;
	overflow: hidden;
}

/* Compact app bar with safe area - fixed at top */
.app-bar-safe {
	padding-top: env(safe-area-inset-top, 0px) !important;
	position: fixed !important;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
}

.app-bar-safe .v-toolbar__content {
	height: 48px !important;
}

/* Branded app bar: circular logo coin + wordmark-style title */
.app-bar-logo {
	box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.7);
	background: #fff;
}
.app-bar-title .v-toolbar-title__placeholder {
	overflow: visible;
}
.app-bar-titlebox {
	display: flex;
	flex-direction: column;
	justify-content: center;
	line-height: 1.08;
}
.app-bar-title__text {
	font-weight: 700;
	font-size: 1rem;
	letter-spacing: 0.2px;
}
.app-bar-title__eyebrow {
	font-size: 0.58rem;
	font-weight: 700;
	letter-spacing: 1.4px;
	text-transform: uppercase;
	opacity: 0.72;
}

/* App-wide warm card treatment: lift flat cards off the cream with a soft,
   warm-tinted shadow + hairline border so content reads as tactile cards
   instead of flat white blocks. Propagates to every screen's flat cards. */
.v-application .v-card.v-card--variant-flat {
	box-shadow: 0 1px 2px rgba(94, 70, 50, 0.05), 0 2px 8px rgba(94, 70, 50, 0.06) !important;
	border: 1px solid rgba(94, 70, 50, 0.06);
}

/* Main content area - scrollable */
.main-content {
	position: fixed !important;
	top: calc(48px + env(safe-area-inset-top, 0px)) !important;
	left: 0;
	right: 0;
	bottom: calc(50px + env(safe-area-inset-bottom, 0px));
	overflow-y: auto !important;
	overflow-x: hidden !important;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: contain;
	background-color: #fbf6ef;
	padding-top: 0 !important;
	padding-bottom: 0 !important;
}

/* Remove default container padding for edge-to-edge content */
.main-content .v-container {
	padding: 0 !important;
}

/* Bottom navigation with home indicator safe area - fixed at bottom */
.bottom-nav-safe {
	position: fixed !important;
	bottom: 0;
	left: 0;
	right: 0;
	padding-bottom: env(safe-area-inset-bottom, 0px) !important;
	height: calc(50px + env(safe-area-inset-bottom, 0px)) !important;
	box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.1) !important;
	z-index: 1000;
}

.bottom-nav-safe .v-btn {
	height: 50px !important;
	min-height: 50px !important;
}

.bottom-nav-safe .v-btn__content {
	flex-direction: column !important;
	gap: 0px;
}

.bottom-nav-safe .v-btn .v-icon {
	font-size: 22px !important;
}

.bottom-nav-safe .v-btn .text-caption {
	font-size: 10px !important;
	line-height: 1.2;
}

.bottom-nav-safe .nav-disabled {
	opacity: 0.4 !important;
	pointer-events: none;
}

/* Page content wrapper - used by individual pages */
.page-content {
	padding: 16px;
	padding-bottom: 80px;
}

/* Card styling for mobile */
.mobile-card {
	border-radius: 0 !important;
	margin-bottom: 8px !important;
}

/* List styling for mobile */
.mobile-list .v-list-item {
	min-height: 56px;
}

/* FAB positioning */
.fab-mobile {
	position: fixed !important;
	bottom: calc(72px + var(--sab, 0px)) !important;
	right: 16px !important;
}
</style>
